/**
 * DNAPage.tsx — /#/dna
 *
 * The renamed "Wall Poster" tab. Surfaces the operational M-PACT Helix
 * with per-gate CP scores, click-to-Q&A side panel, Helix/Tree mode toggle,
 * and a Coaching section below the main surface.
 *
 * Territory-level view: accountId = "territory" by default so the page
 * works without an active account selection. When navigated from an account
 * context (future: pass ?account=<id>), the accountId param is used instead.
 *
 * LocalStorage key: aip:helix:territory (or aip:helix:<id>)
 *
 * Brand: Navy #0B0B0F · Red #DC2626 · bg #FAFAF7
 * Roles: bdr, ae, manager, executive, marketing, se — never 'rep'
 */

import { useEffect, useMemo, useState, useCallback } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { buildPortfolio, buildCoachingTriggers, DEMO_REPS as REPS } from "@/lib/extended";
import { ArrowUpRight } from "lucide-react";
import { Link } from "wouter";
import { api, getCurrentTenant } from "@/lib/session";
import { DnaGlyph } from "@/components/DnaGlyph";

function repSlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, "-");
}
function readRepScore(name: string): number | null {
  try {
    const raw = localStorage.getItem(`aip:pipeline-dna:${repSlug(name)}-score`);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// ── OperationalHelix import ───────────────────────────────────────────────────
// This component ships with the operational helix build (OperationalHelix.tsx).
// If it hasn't merged yet the import below will resolve from:
//   client/src/components/helix/OperationalHelix.tsx
// That file is the canonical source. Do not change the import path.
import OperationalHelix from "@/components/helix/OperationalHelix";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ApiAccount {
  id: number;
  name: string;
}

// LaneS: single source of truth types (mirrors AccountScorecard)
interface CpProgressAnswer {
  question_key: string;
  selected_option: string | null;
  updated_at: string | null;
}
interface CpProgressEntry {
  cp_number: number;
  status: "green" | "amber" | "red" | null;
  answered: number;
  total: number;
  answers: CpProgressAnswer[];
}
interface CpProgress {
  account_id: number;
  composite_score: number;
  cps: CpProgressEntry[];
}

// Status pill colors
const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  green:  { bg: "#DCFCE7", text: "#166534", label: "green" },
  amber:  { bg: "#FEF9C3", text: "#854D0E", label: "orange" },
  red:    { bg: "#FEE2E2", text: "#991B1B", label: "red" },
};

// ── CpProgressPanel — renders per-CP breakdown from /api/accounts/:id/cp-progress
function CpProgressPanel({ accountId, accountName }: { accountId: number; accountName: string }) {
  const [progress, setProgress] = useState<CpProgress | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api<CpProgress>(`/api/accounts/${accountId}/cp-progress`)
      .then((p) => { setProgress(p); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ padding: "20px 0", color: "#6B6B70", fontSize: 13 }}>
        Loading CP progress for {accountName}...
      </div>
    );
  }

  if (!progress) {
    return (
      <div style={{ padding: "20px 0", color: "#6B6B70", fontSize: 13 }}>
        CP progress unavailable for this account.
      </div>
    );
  }

  const activeCps = progress.cps.filter((c) => c.total > 0);
  const totalAnswered = progress.cps.reduce((s, c) => s + c.answered, 0);
  const totalQs = progress.cps.reduce((s, c) => s + c.total, 0);

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6B6B70", marginBottom: 2 }}>
            Checkpoint progress
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0B0B0F" }}>
            {accountName}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#6B6B70" }}>{totalAnswered}/{totalQs} questions answered</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0B0B0F" }}>
            {progress.composite_score.toFixed(2)}
          </div>
          <div style={{ fontSize: 10, color: "#6B6B70" }}>composite score</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 8 }}>
        {activeCps.map((cp) => {
          const sc = cp.status ? STATUS_COLORS[cp.status] : null;
          const pct = cp.total > 0 ? Math.round((cp.answered / cp.total) * 100) : 0;
          return (
            <div
              key={cp.cp_number}
              style={{
                background: sc ? sc.bg : "#F4F4F5",
                borderRadius: 8,
                padding: "10px 12px",
                border: `1px solid ${sc ? sc.bg : "#E5E5E7"}`,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: sc ? sc.text : "#6B6B70" }}>
                CP{cp.cp_number}
              </div>
              {sc && (
                <div style={{ fontSize: 10, fontWeight: 700, color: sc.text, marginTop: 1 }}>
                  {sc.label}
                </div>
              )}
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0B0B0F", marginTop: 4 }}>
                {cp.answered}/{cp.total}
              </div>
              <div style={{ fontSize: 10, color: "#8E8E94" }}>{pct}% answered</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, textAlign: "right" }}>
        <Link
          href={`/account/${accountId}`}
          style={{ fontSize: 12, color: "#DC2626", fontWeight: 600, textDecoration: "none" }}
        >
          Open full account
        </Link>
      </div>
    </div>
  );
}

// ── Coaching section ──────────────────────────────────────────────────────────

function CoachingSection() {
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;

  // Gate fictional cast data to demo tenant only
  const [portfolio, setPortfolio] = useState<ReturnType<typeof buildPortfolio>>([]);
  useEffect(() => {
    if (isDemo) setPortfolio(buildPortfolio());
    // For non-demo tenants, coaching triggers will be empty (no fictional data)
  }, [isDemo]);

  const triggers = useMemo(() => buildCoachingTriggers(portfolio), [portfolio]);
  const [accountIdMap, setAccountIdMap] = useState<Map<string, number>>(new Map());
  const [filterRep, setFilterRep] = useState<"all" | string>("all");

  useEffect(() => {
    api<ApiAccount[]>("/api/accounts")
      .then((accounts) => {
        const m = new Map<string, number>();
        for (const a of accounts) {
          if (a.name) m.set(a.name.toLowerCase().trim(), a.id);
        }
        setAccountIdMap(m);
      })
      .catch(() => {/* not signed in or unavailable — links disabled gracefully */});
  }, []);

  const filtered = useMemo(
    () => filterRep === "all" ? triggers : triggers.filter((t) => t.rep === filterRep),
    [triggers, filterRep],
  );

  const byRep = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of REPS) m[r] = 0;
    for (const t of triggers) m[t.rep] = (m[t.rep] ?? 0) + 1;
    return m;
  }, [triggers]);

  function resolveNumericId(account: string): number | null {
    return accountIdMap.get(account.toLowerCase().trim()) ?? null;
  }

  return (
    <div style={{ marginTop: 40 }}>
      {/* Section header */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "#6B6B70",
            marginBottom: 6,
          }}
        >
          Coaching
        </div>
        <h2
          style={{
            fontSize: 18,
            fontWeight: 800,
            color: "#0B0B0F",
            margin: "0 0 4px",
          }}
        >
          What to coach this week
        </h2>
        <p style={{ fontSize: 12, color: "#6B6B70", margin: 0 }}>
          Auto-generated from portfolio signals: missing CEs, aging deals, top accounts without a stakeholder map, slippage.
        </p>
      </div>

      {/* Rep filter chips — gated: only show fictional rep chips on demo tenant */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
        {[{ key: "all", label: `All · ${triggers.length}` }, ...(isDemo ? REPS.map((r) => ({ key: r, label: `${r} · ${byRep[r] ?? 0}` })) : [])].map(
          ({ key, label }) => {
            const isRep = key !== "all";
            return (
              <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {isRep && (
                  <a
                    href={`#/pipeline-dna/${repSlug(key)}`}
                    aria-label={`Open Pipeline DNA for ${key}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 2,
                      borderRadius: 6,
                      color: "#0B0B0F",
                      textDecoration: "none",
                    }}
                  >
                    <DnaGlyph score={readRepScore(key)} size={20} ariaLabel={`${key} pipeline DNA`} />
                  </a>
                )}
                <button
                  onClick={() => setFilterRep(key as "all" | string)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 9999,
                    fontSize: 11,
                    fontWeight: 700,
                    border: filterRep === key ? "1px solid rgba(220,38,38,0.6)" : "1px solid #E5E5E7",
                    background: filterRep === key ? "rgba(220,38,38,0.08)" : "transparent",
                    color: filterRep === key ? "#0B0B0F" : "#6B6B70",
                    cursor: "pointer",
                    transition: "all 0.13s",
                  }}
                >
                  {label}
                </button>
              </span>
            );
          },
        )}
      </div>

      {/* Trigger list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.slice(0, 12).map((t, i) => {
          const numericId = resolveNumericId(t.account);
          return (
            <div
              key={`${i}-${t.accountId}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                background: "#FFFFFF",
                border: "1px solid #E5E5E7",
                borderRadius: 10,
              }}
            >
              {/* Severity bar */}
              <div
                style={{
                  width: 4,
                  alignSelf: "stretch",
                  borderRadius: 2,
                  background:
                    t.severity === "high" ? "#F87171" : t.severity === "medium" ? "#FBBF24" : "#8E8E94",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8E8E94", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  {t.rep}
                </div>
                <div style={{ fontSize: 13, color: "#0B0B0F", marginTop: 2, lineHeight: 1.4 }}>{t.message}</div>
              </div>
              {numericId !== null ? (
                <Link
                  href={`/account/${numericId}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "6px 12px",
                    borderRadius: 7,
                    border: "1px solid #E5E5E7",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#525258",
                    textDecoration: "none",
                    flexShrink: 0,
                    transition: "all 0.13s",
                  }}
                >
                  {t.account} <ArrowUpRight size={11} />
                </Link>
              ) : (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "6px 12px",
                    borderRadius: 7,
                    border: "1px solid #E5E5E7",
                    fontSize: 11,
                    color: "#8E8E94",
                    flexShrink: 0,
                    opacity: 0.5,
                  }}
                  title={`${t.account} is not in your live account list`}
                >
                  {t.account} <ArrowUpRight size={11} />
                </span>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div
            style={{
              padding: "32px 24px",
              textAlign: "center",
              fontSize: 13,
              color: "#8E8E94",
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 10,
            }}
          >
            {isDemo ? "No coaching triggers for this filter." : "No coaching triggers yet. Triggers populate once accounts have logged activity and qualification answers."}
          </div>
        )}
        {filtered.length > 12 && (
          <div style={{ textAlign: "center", fontSize: 12, color: "#6B6B70", padding: "8px 0" }}>
            Showing 12 of {filtered.length} triggers.{" "}
            <Link href="/coaching" style={{ color: "#DC2626", textDecoration: "none", fontWeight: 600 }}>
              View all
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DNAPage() {
  // LaneS: read ?account=<id> from hash query string for single-account drill-in.
  // When present, show a CpProgressPanel sourced from /api/accounts/:id/cp-progress
  // (single source of truth). Territory view remains the default.
  const [drillAccount, setDrillAccount] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    function parseHashAccount() {
      try {
        const hash = window.location.hash; // e.g. "#/dna?account=31&name=Dwyer-Omega"
        const qIndex = hash.indexOf("?");
        if (qIndex === -1) { setDrillAccount(null); return; }
        const params = new URLSearchParams(hash.slice(qIndex + 1));
        const idStr = params.get("account");
        const nameStr = params.get("name") ?? "";
        const id = idStr ? Number(idStr) : NaN;
        if (Number.isFinite(id) && id > 0) {
          setDrillAccount({ id, name: nameStr });
        } else {
          setDrillAccount(null);
        }
      } catch {
        setDrillAccount(null);
      }
    }
    parseHashAccount();
    window.addEventListener("hashchange", parseHashAccount);
    return () => window.removeEventListener("hashchange", parseHashAccount);
  }, []);

  // "territory" is the global scope key used in localStorage for the helix view.
  const accountId = "territory";

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Opportunity DNA" }]}
    >
      <div className="max-w-[1400px] w-full mx-auto px-0 sm:px-2">
        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#6B6B70",
              marginBottom: 6,
            }}
          >
            Mi6 Attilius · M-PACT Intelligence
          </div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 900,
              color: "#0B0B0F",
              margin: "0 0 6px",
              letterSpacing: "-0.01em",
            }}
          >
            Opportunity DNA
          </h1>
          <p style={{ fontSize: 13, color: "#6B6B70", margin: 0, lineHeight: 1.5 }}>
            The full operating surface — composite score and per-Checkpoint qualification across your territory.
            View only: to update answers, open an individual account.
          </p>
        </div>

        {/* OperationalHelix surface */}
        {/*
          OperationalHelix is the canonical operational helix component.
          Import path: @/components/helix/OperationalHelix
          File:        client/src/components/helix/OperationalHelix.tsx
          Props:       accountId, accountName?, industry?, initialAnswers?
          If the per-account helix build agent merges additional props,
          update the accountId prop here to reflect the active account context.
        */}
        {/*
          readOnly=true — Bryan 2026-05-27: Opportunity DNA is a VIEW-ONLY surface.
          No CP click-to-open. No question-bank pop-ups. Interactive mode lives
          on the account detail page; this page is for looking, not editing.
        */}
        {/*
          W61: helixOrientation="bottom-up" — CP1 at waterline (bottom),
          CP10 at roof (top), per Tower canon. /galaxy and /account pages
          remain top-down (Galaxy metaphor).
        */}
        <OperationalHelix
          accountId={accountId}
          accountName="Territory Overview"
          industry={undefined}
          readOnly={true}
          helixOrientation="bottom-up"
        />

        {/* Coaching section */}
        {/* LaneS: single-account drill-in panel — when ?account=<id> is present in the URL,
            render the cp-progress panel from the single source of truth endpoint.
            This surfaces the same data as AccountScorecard so both views agree. */}
        {drillAccount && (
          <div
            style={{
              marginTop: 28,
              padding: "20px 24px",
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6B6B70" }}>
                Account drill-in
              </div>
              <button
                onClick={() => {
                  const hash = window.location.hash;
                  const qIndex = hash.indexOf("?");
                  window.location.hash = qIndex === -1 ? hash : hash.slice(0, qIndex);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 11,
                  color: "#8E8E94",
                  padding: "2px 6px",
                }}
              >
                Clear
              </button>
            </div>
            <CpProgressPanel accountId={drillAccount.id} accountName={drillAccount.name} />
          </div>
        )}

        <CoachingSection />
      </div>
    </OperatorShell>
  );
}
